import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { AppSplash } from './AppSplash';

/**
 * Gate for everything behind sign-in. Replaces `RequireChatbot`.
 *
 * The intended destination is preserved in router *state* rather than a
 * `?returnTo=` query string: state is opaque and not user-editable, so it
 * cannot be turned into an open redirect by handing someone a crafted link.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  // `AuthProvider` already holds the splash while resolving, so this branch is
  // unreachable in the normal boot. It stays as cheap insurance against the
  // provider being moved or a re-validation path being added later.
  if (status === 'resolving') return <AppSplash />;

  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

/** Keeps a signed-in user off the login and register screens. */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { status } = useAuth();

  if (status === 'resolving') return <AppSplash />;
  if (status === 'authenticated') return <Navigate to="/app" replace />;

  return <>{children}</>;
}
